/**
 * Preview inline de Binary (evita dialog sobre dialog).
 * @module DocumentInlinePreview
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { GetBinary } from "@/services/fhir/patientsummary/Binary/GetBinary";

interface DocumentInlinePreviewProps {
  title: string;
  urlBinary: string;
  onClose: () => void;
}

export default function DocumentInlinePreview({
  title,
  urlBinary,
  onClose,
}: DocumentInlinePreviewProps) {
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestIdRef = useRef(0);

  const fetchBinary = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    setLoading(true);
    setError(false);
    setSrc(null);
    try {
      const response = await GetBinary({ urlBinary });
      if (requestId !== requestIdRef.current) return;
      if (response?.data && response?.contentType) {
        setSrc(`data:${response.contentType};base64,${response.data}`);
      } else {
        setError(true);
      }
    } catch {
      if (requestId !== requestIdRef.current) return;
      setError(true);
    } finally {
      if (requestId === requestIdRef.current) setLoading(false);
    }
  }, [urlBinary]);

  useEffect(() => {
    void fetchBinary();
    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchBinary]);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        overflow: "hidden",
        bgcolor: "background.paper",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        px={1.5}
        py={0.75}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>
          {title}
        </Typography>
        <IconButton size="small" aria-label="Fechar preview" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Stack>

      {loading && (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={28} />
        </Stack>
      )}

      {!loading && error && (
        <Alert
          severity="error"
          sx={{ m: 1 }}
          action={
            <Button color="inherit" size="small" onClick={() => void fetchBinary()}>
              Tentar novamente
            </Button>
          }
        >
          Não foi possível carregar o documento.
        </Alert>
      )}

      {!loading && !error && src && (
        <iframe
          title={title}
          src={src}
          width="100%"
          height={360}
          style={{ border: "none", display: "block" }}
        />
      )}
    </Box>
  );
}
